'use client'

import React from 'react'

import { Box } from '@chakra-ui/react'

import { tableRowStyles } from './Table.styles'
import {
  useTableColorScheme,
  useTableDensity,
  useTableVariant,
} from './TableContext'

export type TableRowActivationEvent =
  | React.MouseEvent<HTMLElement>
  | React.KeyboardEvent<HTMLElement>

export type TableRowProps = {
  children: React.ReactNode
  // Receives the triggering event so callers can read modifier keys
  // (e.g. Cmd/Ctrl + click to open the row's target in a new tab).
  onClick?: (event: TableRowActivationEvent) => void
  // Non-primary button clicks (e.g. middle-click). Callers should check
  // `event.button`, as browsers also fire auxclick for the right button.
  onAuxClick?: (event: React.MouseEvent<HTMLElement>) => void
  isSelected?: boolean
  isAlternate?: boolean
}

export const TableRow = ({
  children,
  onClick,
  onAuxClick,
  isSelected = false,
  isAlternate = false,
}: TableRowProps) => {
  const colorScheme = useTableColorScheme()
  const density = useTableDensity()
  const variant = useTableVariant()
  return (
    <Box
      {...tableRowStyles(
        colorScheme,
        !!onClick,
        isSelected,
        isAlternate,
        density,
        variant
      )}
      onAuxClick={onAuxClick}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e: React.KeyboardEvent<HTMLElement>) => {
              if (
                e.target === e.currentTarget &&
                (e.key === 'Enter' || e.key === ' ')
              ) {
                e.preventDefault()
                onClick(e)
              }
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </Box>
  )
}
